import Button from 'tio-reimagined/components/button';
import { fn, concat } from '@ember/helper';
import { eq, and } from 'tio-ui/utilities';
import { on } from '@ember/modifier';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { PhUserSwitch, PhX } from 'ember-phosphor-icons';
import { t } from 'ember-intl';
import Component from '@glimmer/component';
import { dropTask, restartableTask, timeout } from 'ember-concurrency';
import Select from 'tio-reimagined/components/select';
import { task } from 'reactiveweb/ember-concurrency';
import { trackedFunction } from 'reactiveweb/function';
import { lookupEmployeesByIds } from '../../../../utils/employee-lookup';
import type { EmployeeLookupResult } from '../../../../utils/employee-lookup';
import type {
  ApplicationApprovalSummary,
  ApplicationApprovalSummaryApprover,
} from '../../../../types/tuition-assistance';
import type StoreService from '@ember-data/store';
import type TasApplicationModel from '../../../../models/tas-application.ts';
import type TasProgramInstance from '../../../../models/tas-program-instance.ts';
import { DrawerHeader, DrawerBody } from 'tio-reimagined/components/drawer';
import type EmployeeModel from '../../../../models/employee.ts';
import type { TaskInstance } from 'reactiveweb/ember-concurrency';
import type { NotificationsService } from 'tio-reimagined/components/notifications';

const INPUT_DEBOUNCE = 250;

/**
 * Approval Queue Status drawer component
 * Displays approval workflow with better visual hierarchy
 * Includes inline reassignment UI to avoid modal/drawer conflicts
 */
export interface ApprovalQueueStatusDrawerSignature {
  Args: {
    application: TasApplicationModel | TasProgramInstance;
    consumingApp: string;
  };
}

export default class ApprovalQueueStatusDrawer extends Component<ApprovalQueueStatusDrawerSignature> {
  @service declare store: StoreService;
  @service declare notifications: NotificationsService;

  // Reassignment state - now owned by the drawer
  @tracked selectedApprover: ApplicationApprovalSummaryApprover | null = null;
  @tracked showReassignmentUI = false;
  @tracked selectedApproverId = '';
  @tracked searchTerm = '';

  /**
   * Load employee names for all approvers via OpenSearch
   * This runs once and fetches all employee data in a single query
   */
  employeeDataState = trackedFunction(this, async () => {
    // Collect all unique employee IDs from approvers
    const allApprovers = [...this.approvalQueue.affirmed, ...this.approvalQueue.possible_reviewers];

    const employeeIds = Array.from(
      new Set(
        allApprovers
          .map((approver) => approver.employee_id)
          .filter((id): id is number => typeof id === 'number')
      )
    );

    if (!employeeIds.length) {
      return new Map<string, EmployeeLookupResult>();
    }

    // Single OpenSearch query for all employees
    return await lookupEmployeesByIds(this.store, employeeIds);
  });

  /**
   * Get display name for an approver
   * Falls back to "Employee ID: {id}" if name not found
   */
  getApproverName = (approverId: number): string => {
    // Convert to string for map lookup
    const idString = String(approverId);

    console.log('[getApproverName] Looking up:', approverId, 'as string:', idString);
    console.log('[getApproverName] State:', {
      isLoading: this.employeeDataState.isLoading,
      isError: this.employeeDataState.isError,
      hasValue: !!this.employeeDataState.value,
      mapSize: this.employeeDataState.value?.size,
    });

    // Check loading state
    if (this.employeeDataState.isLoading) {
      console.log('[getApproverName] Still loading...');
      return `Employee ID: ${approverId}`;
    }

    // Check error state
    if (this.employeeDataState.isError) {
      console.log('[getApproverName] Error state:', this.employeeDataState.error);
      return `Employee ID: ${approverId}`;
    }

    // Get name from loaded data
    const employeeData = this.employeeDataState.value?.get(idString);
    console.log('[getApproverName] Found data:', employeeData);

    return employeeData?.fullName || `Employee ID: ${approverId}`;
  };

  get approvalQueue(): ApplicationApprovalSummary {
    const { application } = this.args;

    switch (application.state) {
      case 'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL':
        return application.evidenceApproval;
      case 'TAS.ApplicationState.PENDING_COURSES_APPROVAL':
        return application.coursesApproval;
      case 'TAS.ProgramInstanceState.PENDING_PROGRAM_APPROVAL':
        return application.programApproval;
      case 'TAS.ProgramInstanceState.PENDING_COMPLETION_APPROVAL':
        return application.completionApproval;
      case 'TAS.ProgramInstanceState.PENDING_WITHDRAWAL_APPROVAL':
        return application.withdrawalApproval;
      default:
        return {
          affirmed: [],
          current_reviewers: [],
          possible_reviewers: [],
          required: [],
          approved: false,
        };
    }
  }

  get affirmedReviewers() {
    return this.approvalQueue.affirmed;
  }

  get pendingReviewersByLevel() {
    const { affirmed, possible_reviewers } = this.approvalQueue;

    let tioApproverFound = false;

    const pendingReviewersQueue = possible_reviewers.filter((reviewer) => {
      const hasAlreadyApproved = affirmed.some(
        (approved) => approved.employee_id === reviewer.employee_id
      );
      const hasMatchingOrdinal = affirmed.some((approved) => approved.ordinal === reviewer.ordinal);

      if (reviewer.dot === 'TAS.Approver.TIO_APPROVER') {
        if (tioApproverFound) {
          return false;
        } else {
          tioApproverFound = true;
        }
      }
      return !hasAlreadyApproved && !hasMatchingOrdinal;
    });

    return this.groupReviewersByOrdinal(pendingReviewersQueue);
  }

  groupReviewersByOrdinal(reviewers: ApplicationApprovalSummaryApprover[]) {
    const minOrdinal = Math.min(...reviewers.map((reviewer) => reviewer.ordinal));

    const groupedReviewers = reviewers.reduce<Record<number, ApplicationApprovalSummaryApprover[]>>(
      (result, reviewer) => {
        const { ordinal } = reviewer;
        const normalizedOrdinal = ordinal - minOrdinal;

        if (!(normalizedOrdinal in result)) {
          result[normalizedOrdinal] = [];
        }
        result[normalizedOrdinal]?.push(reviewer);

        return result;
      },
      {}
    );

    return groupedReviewers;
  }

  get companyId() {
    const application = this.args.application;
    if (application.modelName === 'tas-program-instance') {
      // @ts-expect-error: Nested relationship type not fully typed
      return application.employee.company.id;
    }
    // @ts-expect-error: Nested relationship type not fully typed
    return application.tasProgramInstance.employee.company.id;
  }

  getLevelNumber = (reviewers: ApplicationApprovalSummaryApprover[]) => {
    return reviewers[0]?.ordinal ?? 0;
  };

  handleReassignClick = (approver: ApplicationApprovalSummaryApprover) => {
    // Update local state to show reassignment UI
    this.selectedApprover = approver;
    this.showReassignmentUI = true;
  };

  cancelReassignment = () => {
    // Clear local reassignment state
    this.selectedApprover = null;
    this.showReassignmentUI = false;
    this.selectedApproverId = '';
    this.searchTerm = '';
  };

  onFilterChange = (term: string) => {
    this.searchTerm = term;
  };

  handleSelectionChange = async (key: string | null) => {
    this.selectedApproverId = key || '';
  };

  insertReviewer = dropTask(async () => {
    if (!this.selectedApproverId) return;
    const adapter =
      this.args.application.modelName === 'tas-application'
        ? this.store.adapterFor('tas-application')
        : this.store.adapterFor('tas-program-instance');

    return adapter.insertReviewer(
      this.args.application,
      this.selectedApproverId,
      this.selectedApprover!.ordinal
    );
  });

  searchApprovers = restartableTask(async (search: string) => {
    if (!search) {
      return [];
    }
    await timeout(INPUT_DEBOUNCE);
    const results = await this.store.query<EmployeeModel>('employee', {
      filter: { company_id: this.companyId, search },
    });
    return results.map((employee) => ({
      key: employee.id!,
      label: employee.fullName || employee.id!,
    }));
  });

  fetchApproversTask: TaskInstance<Array<{ key: string; label: string }>> = task(
    this,
    this.searchApprovers,
    () => [this.searchTerm]
  );

  get approverItems() {
    return this.fetchApproversTask.value ?? [];
  }

  confirmReassignment = async () => {
    if (!this.selectedApproverId) return;

    try {
      await this.insertReviewer.perform();
      this.notifications.add('Successfully reassigned approver', {
        appearance: 'success',
      });
      this.cancelReassignment();
      // Reload application to reflect changes
      await this.args.application.reload();
    } catch (e) {
      console.error(e);
      this.notifications.add('Something went wrong', {
        appearance: 'error',
      });
    }
  };

  <template>
    <DrawerHeader>
      <h2 class="text-lg font-semibold text-gray-800">Approval Queue</h2>
    </DrawerHeader>

    <DrawerBody>
      <div class="-mt-6 mb-6">
        {{#if this.affirmedReviewers.length}}
          <div class="px-6 pt-6 pb-4">
            <h3 class="text-sm font-semibold text-gray-700 mb-3">Completed Approvals</h3>
            <div class="space-y-2">
              {{#each this.affirmedReviewers as |approver|}}
                <div
                  class="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800"
                >
                  <div class="flex items-center gap-3">
                    <div
                      class="flex-shrink-0 w-8 h-8 bg-green-100 dark:bg-green-800 rounded-full flex items-center justify-center"
                    >
                      <span
                        class="text-green-700 dark:text-green-300 text-xs font-semibold"
                      >✓</span>
                    </div>
                    <div>
                      <div class="text-sm font-medium text-gray-900">
                        {{this.getApproverName approver.employee_id}}
                      </div>
                      <div class="text-xs text-gray-500">
                        {{t (concat "tuition_assistance.approval_queue.roles." approver.dot)}}
                      </div>
                    </div>
                  </div>
                </div>
              {{/each}}
            </div>
          </div>
          <div class="border-t border-gray-200"></div>
        {{/if}}

        <div class="px-6 py-6">
          <h3 class="text-sm font-semibold text-gray-700 mb-3">Pending Approvers</h3>

          <div class="space-y-6">
            {{#each-in this.pendingReviewersByLevel as |_levelIndex reviewers|}}
              <div class="space-y-3">
                <div class="flex items-center gap-2">
                  <div
                    class="flex-shrink-0 w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center border-2 border-blue-600 dark:border-blue-500"
                  >
                    <span
                      class="text-blue-700 dark:text-blue-300 text-sm font-semibold"
                    >{{this.getLevelNumber reviewers}}</span>
                  </div>
                  <div class="text-sm font-medium text-gray-700">
                    Level
                    {{this.getLevelNumber reviewers}}
                    Approvers -
                    {{reviewers.length}}
                    {{if (eq reviewers.length 1) "approver" "approvers"}}
                    available
                  </div>
                </div>

                <div class="ml-10 space-y-2">
                  {{#each reviewers as |approver|}}
                    <div class="space-y-2">
                      <div
                        class="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors"
                      >
                        <div class="flex items-center gap-3 flex-1">
                          <div
                            class="flex-shrink-0 w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center"
                          >
                            <span
                              class="text-gray-600 text-xs font-semibold"
                            >{{approver.ordinal}}</span>
                          </div>
                          <div class="flex-1 min-w-0">
                            <div class="text-sm font-medium text-gray-900 truncate">
                              {{this.getApproverName approver.employee_id}}
                            </div>
                            <div class="text-xs text-gray-500 truncate">
                              {{t (concat "tuition_assistance.approval_queue.roles." approver.dot)}}
                            </div>
                          </div>
                        </div>
                        <button
                          type="button"
                          class="flex-shrink-0 flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:text-blue-400 dark:hover:text-blue-300 dark:hover:bg-blue-900/20 rounded-md transition-colors"
                          {{on "click" (fn this.handleReassignClick approver)}}
                        >
                          <PhUserSwitch @size="16" />
                          Reassign
                        </button>
                      </div>

                      {{! Inline reassignment UI - shows immediately when approver is selected }}
                      {{#if (and (eq this.selectedApprover approver) this.showReassignmentUI)}}
                        <div
                          class="mt-2 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg"
                        >
                          <div class="flex items-center justify-between mb-3">
                            <h4 class="text-sm font-semibold text-gray-900">
                              Reassign
                              {{t (concat "tuition_assistance.approval_queue.roles." approver.dot)}}
                            </h4>
                            <button
                              type="button"
                              class="text-gray-400 hover:text-gray-600"
                              {{on "click" this.cancelReassignment}}
                            >
                              <PhX @size="20" />
                            </button>
                          </div>

                          <Select
                            data-test-approver-select
                            @label={{t
                              "tuition_assistance.approval_queue.select_replacement_approver"
                            }}
                            @searchable={{true}}
                            @isLoading={{this.fetchApproversTask.isRunning}}
                            @selected={{this.selectedApproverId}}
                            @items={{this.approverItems}}
                            @onSearch={{this.onFilterChange}}
                            @onChange={{this.handleSelectionChange}}
                            @clearable={{true}}
                            @placeholder="Search for an employee..."
                            @searchDebounce={{0}}
                          />

                          <div class="flex gap-2 mt-3">
                            <Button
                              @intent="primary"
                              @size="sm"
                              @isRunning={{this.insertReviewer.isRunning}}
                              {{on "click" this.confirmReassignment}}
                            >
                              Confirm Reassignment
                            </Button>
                            <Button
                              @appearance="outlined"
                              @size="sm"
                              {{on "click" this.cancelReassignment}}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      {{/if}}
                    </div>
                  {{/each}}
                </div>
              </div>
            {{/each-in}}
          </div>
        </div>
      </div>
    </DrawerBody>
  </template>
}
